/**
 * EditRenderer — draws a SplatEditModel with the edit shaders.
 *
 * The edit session renders its own entity, built from the model's flat
 * columns, instead of patching the scene's splat. That makes every source
 * format (PLY, compressed PLY, SOG, SPZ, KHR GLB, LOD streams, LCC2 imports)
 * look identical to the editor: whatever was loaded is decoded once into the
 * model, and the scene's original entity is simply disabled for the session.
 *
 * The entity lives on its own layer so the GPU pick pass sees splat indices
 * only, and uses a non-unified gsplat component so its material can carry the
 * edit chunks and its sorter can drop deleted splats and follow moved ones.
 */
import * as pc from 'playcanvas';
import type { SplatEditModel } from '../core/SplatEditModel';
import { SplatState } from '../core/SplatState';
import { gradeRows, type GradeTerms } from '../core/ColorGrade';
import { SelectionOutline } from './SelectionOutline';
import {
    PALETTE_ROW, PICK_OP_ALL,
    centerGLSL, vertexGLSL, fragmentGLSL,
    centerWGSL, vertexWGSL, fragmentWGSL,
} from './editShaders';

export interface EditDisplayOptions {
    showGaussians: boolean;
    showCenters: boolean;
    centerSize: number;
    /** 0 = off, otherwise ring thickness as a fraction of the footprint (0..1). */
    ringSize: number;
    selectedColor: [number, number, number];
    selectionBlend: number;
    unselectedColor: [number, number, number];
    unselectedBlend: number;
    lockedColor: [number, number, number];
    lockedAlpha: number;
    /** Rings only on the selection (SuperSplat "Selection Rings"), used when ringSize is 0. */
    selectionRings: boolean;
    /** Center dots only on the selection (SuperSplat "Selection Centers"). */
    selectionCenters: boolean;
    /** SH bands to display (-1 = all the data has). */
    shBands: number;
    /** Draw the bounding box of the selection (or whole splat) with its dimensions. */
    showBounds: boolean;
    /** Outline the silhouette of the selection (SuperSplat "Selection Outline"). */
    selectionOutline: boolean;
}

export const DEFAULT_DISPLAY: EditDisplayOptions = {
    showGaussians: true,
    showCenters: false,
    centerSize: 2,
    ringSize: 0,
    selectedColor: [1, 1, 0],
    selectionBlend: 0.5,
    unselectedColor: [0, 0, 1],
    unselectedBlend: 0,
    lockedColor: [0, 0, 0],
    lockedAlpha: 0.15,
    selectionRings: false,
    selectionCenters: false,
    shBands: -1,
    showBounds: false,
    selectionOutline: false,
};

const LAYER_NAME = 'SplatEdit';
/** Longest a previous build stays on screen waiting for the rebuilt one's first sort. */
const STAND_IN_TIMEOUT_MS = 2000;
const OVERLAY_LAYER_NAME = 'SplatEditOverlay';

const DEPTH_LAYER_NAME = 'SplatEditDepth';
/** Opacity above which a splat of the edited scan hides what is behind it (the engine's own prepass uses 0.3). */
export const SPLAT_EDIT_DEPTH_CLIP = 0.3;

export type SplatEditLayerRole = 'edit' | 'overlay' | 'depth';

/**
 * Where the edit layers go in the composition. The edited scan's layer sits right BEFORE World's
 * transparent pass, so the scene's other splats (unified, in World: a part just cut out with
 * Separate, splat models) draw after it. Splats write no depth: whichever draws later paints over
 * the other, and with the scan drawn after World, the scan around and behind a separated object hid
 * it completely ("Separate deleted my splats"). The overlay (selection tints, centres) follows the
 * edit layer's transparent pass, and the depth layer follows both: it writes the scan's surface
 * depth after the scan has drawn, so World's splats behind that surface stay hidden.
 */
export function insertSplatEditLayer(layers: pc.LayerComposition, layer: pc.Layer, role: SplatEditLayerRole): void {
    const world = layers.getLayerByName('World');
    if (role === 'overlay') {
        const edit = layers.getLayerByName(LAYER_NAME);
        const after = edit ?? world;
        const t = after ? layers.getTransparentIndex(after) : layers.layerList.length - 1;
        layers.insertOpaque(layer, Math.max(0, t + 1));
        layers.insertTransparent(layer, layers.getOpaqueIndex(layer) + 1);
    } else if (world) {
        const t = layers.getTransparentIndex(world);
        layers.insertOpaque(layer, t);
        layers.insertTransparent(layer, layers.getOpaqueIndex(layer) + 1);
    } else {
        layers.push(layer);
    }
}

/**
 * A depth-only copy of the edit splat's material: the same splats, order and uniforms, but each
 * writes its centre's depth where its opacity reaches SPLAT_EDIT_DEPTH_CLIP and no colour. The
 * uniform records are shared with `source`, so every later setParameter on the main material
 * (the sort's splat count, display options, previews, palettes) reaches this one too.
 */
export function splatEditDepthMaterial(source: pc.Material): pc.Material {
    const material = source.clone();
    const params = (material as unknown as { parameters: Record<string, unknown> }).parameters;
    const shared = (source as unknown as { parameters: Record<string, unknown> }).parameters;
    for (const name of Object.keys(shared)) params[name] = shared[name];
    material.setParameter('splatEditDepthClip', SPLAT_EDIT_DEPTH_CLIP);
    material.setDefine('SPLAT_EDIT_DEPTH', '');
    // colour is never written: skip the spherical harmonics
    material.setDefine('SH_BANDS', '0');
    material.blendType = pc.BLEND_NONE;
    material.redWrite = false;
    material.greenWrite = false;
    material.blueWrite = false;
    material.alphaWrite = false;
    material.depthTest = true;
    material.depthWrite = true;
    material.update();
    return material;
}

/**
 * True when a unified splat other than the scan being edited draws in World (a splat model, a
 * part cut out with Separate). Only then is the scan's depth worth writing.
 */
export function hasOtherWorldSplats(app: pc.AppBase): boolean {
    const world = app.scene.layers.getLayerByName('World');
    const store = (app.systems as unknown as { gsplat?: { store?: Record<string, { entity: pc.Entity }> } }).gsplat?.store;
    if (!world || !store) return false;
    for (const key in store) {
        const entity = store[key]?.entity;
        const gsplat = entity?.gsplat as unknown as { enabled?: boolean; unified?: boolean; layers?: number[] } | undefined;
        if (gsplat?.enabled && gsplat.unified && entity.enabled && (gsplat.layers ?? []).includes(world.id)) return true;
    }
    return false;
}

/** One build's depth draw: a mesh instance sharing the edit splat's mesh, order and uniforms. */
interface DepthDraw {
    meshInstance: pc.MeshInstance;
    material: pc.Material;
    /** The edit splat's own mesh instance, whose sorted count the depth draw follows. */
    main: pc.MeshInstance;
}

export class EditRenderer {
    readonly app: pc.AppBase;
    readonly model: SplatEditModel;
    readonly layer: pc.Layer;
    readonly overlayLayer: pc.Layer;
    /**
     * Writes the edited scan's surface depth after it has drawn, so the scene's other splats (splat
     * models, parts cut out with Separate) are hidden where the scan is in front of them and show
     * where they are in front of it. Enabled only while such splats are on (hasOtherWorldSplats).
     */
    readonly depthLayer: pc.Layer;

    private _entity: pc.Entity;
    private _depth: DepthDraw | null = null;
    private readonly _parent: pc.Entity;
    /**
     * The previous build, still drawn after a rebuild until the new gsplat
     * instance has its first sort (a fresh instance draws nothing until then),
     * so adding or removing rows never blanks the splat.
     */
    private _standIn: { entity: pc.Entity; resource: pc.GSplatResource | null; textures: pc.Texture[]; depth: DepthDraw | null; timer: ReturnType<typeof setTimeout> } | null = null;
    /** Run once the model as it is now is on screen (see whenRedrawn). */
    private _redrawn: Array<() => void> = [];
    private _centersEntity: pc.Entity | null = null;
    private _maskEntity: pc.Entity | null = null;
    private _outline: SelectionOutline | null = null;
    private _resource: pc.GSplatResource | null = null;
    private _gsplatData: pc.GSplatData | null = null;
    private _stateTex: pc.Texture | null = null;
    private _transformIndexTex: pc.Texture | null = null;
    private _colorIndexTex: pc.Texture | null = null;
    private _transformPaletteTex: pc.Texture | null = null;
    private _colorPaletteTex: pc.Texture | null = null;
    private _dims = { x: 0, y: 0 };
    private _synced = { state: -1, marks: -1, transform: -1, color: -1, rows: -1, transformPalette: -1, colorPalette: -1 };
    private _deletedKey = '';
    private _display: EditDisplayOptions = { ...DEFAULT_DISPLAY };
    private _previewTransform: Float32Array | null = null;
    private _previewTransformAll = false;
    private _previewGrade: GradeTerms | null = null;
    private _previewGradeAll = false;
    private _pickOp = PICK_OP_ALL;
    private _cameras: pc.CameraComponent[] = [];
    private _baseLocal = new pc.Mat4();
    private _dataTransform = new pc.Mat4();
    private _onPreRender: () => void;
    private _onPreRenderLayer: (camera: pc.CameraComponent, layer: pc.Layer) => void;

    /** Fired after GPU resources are rebuilt (row count changed). */
    onRebuilt?: () => void;

    constructor(
        app: pc.AppBase,
        model: SplatEditModel,
        options: { parent: pc.Entity; localTransform: pc.Mat4; cameras: pc.CameraComponent[] },
    ) {
        this.app = app;
        this.model = model;
        this._cameras = options.cameras;
        this._baseLocal.copy(options.localTransform);

        this.layer = this._ensureLayer(LAYER_NAME, 'edit');
        this.overlayLayer = this._ensureLayer(OVERLAY_LAYER_NAME, 'overlay');
        this.depthLayer = this._ensureLayer(DEPTH_LAYER_NAME, 'depth');
        this.depthLayer.enabled = false;

        this._parent = options.parent;
        this._entity = new pc.Entity('splat-edit');
        options.parent.addChild(this._entity);
        this._applyEntityTransform();

        this._build();

        this._onPreRender = () => this.sync();
        app.on('prerender', this._onPreRender);
        // the sorted count lands on the edit splat's mesh instance after 'prerender' (in the
        // renderer's GPU update): the depth draws copy it right before their layer renders
        this._onPreRenderLayer = (_camera, layer) => {
            if (layer !== this.depthLayer) return;
            for (const depth of [this._depth, this._standIn?.depth]) {
                if (depth) depth.meshInstance.instancingCount = depth.main.instancingCount;
            }
        };
        app.scene.on('prerender:layer', this._onPreRenderLayer);
    }

    // ─── Public API ─────────────────────────────────────────

    /** The entity drawing the model (replaced when rows are added or removed). */
    get entity(): pc.Entity {
        return this._entity;
    }

    /**
     * Run `callback` once the model as it is now is on screen: after the
     * rebuild an added or removed row triggers has drawn sorted, otherwise on
     * the next frame. Used to drop a stroke preview in the same frame its
     * result appears.
     */
    whenRedrawn(callback: () => void): void {
        this._redrawn.push(callback);
        this.requestRender();
    }

    get resource(): pc.GSplatResource | null {
        return this._resource;
    }

    get textureDimensions(): { x: number; y: number } {
        return this._dims;
    }

    /** Whole-splat data transform G (column-major), applied after the source frame. */
    setDataTransform(m: ArrayLike<number>): void {
        this._dataTransform.set(Array.from(m));
        this._applyEntityTransform();
    }

    get dataTransform(): Float32Array {
        return Float32Array.from(this._dataTransform.data);
    }

    get baseLocalTransform(): pc.Mat4 {
        return this._baseLocal.clone();
    }

    setDisplay(options: Partial<EditDisplayOptions>): void {
        const prevBands = this._display.shBands;
        this._display = { ...this._display, ...options };
        const centersOn = this._display.showCenters || this._display.selectionCenters;
        // overlays are destroyed when off, not disabled: a rebuild (rows added or
        // removed) can't give a disabled entity's gsplat component an instance
        if (centersOn && !this._centersEntity) this._createCenters();
        else if (!centersOn && this._centersEntity) this._destroyCenters();
        if (this._display.selectionOutline && !this._outline) this._createOutline();
        else if (!this._display.selectionOutline && this._outline) this._destroyOutline();
        if (this._outline) this._outline.color = this._display.selectedColor;
        if (this._display.shBands !== prevBands) this._applyShBands();
        this._applyUniforms();
        this.requestRender();
    }

    /** Limit the SH bands drawn (display only; saving keeps what the save options say). */
    private _applyShBands(): void {
        const dataBands = this.model.shBands;
        const want = this._display.shBands < 0 ? dataBands : Math.min(dataBands, this._display.shBands);
        for (const m of this._materials()) {
            m.setDefine('SH_BANDS', String(want));
            m.update();
        }
    }

    get display(): EditDisplayOptions {
        return { ...this._display };
    }

    /**
     * Live transform (model space) shown while a gizmo is dragged, or null.
     * `all` previews it on every live splat instead of only the selection.
     */
    setPreviewTransform(m: ArrayLike<number> | null, all = false): void {
        this._previewTransform = m ? Float32Array.from(m) : null;
        this._previewTransformAll = all;
        this._applyUniforms();
        this.requestRender();
    }

    /** Live colour grade shown before it is applied (selection, or everything with `all`), or null. */
    setPreviewGrade(terms: GradeTerms | null, all = false): void {
        this._previewGrade = terms ? { m: terms.m.slice(), offset: { ...terms.offset }, transparency: terms.transparency } : null;
        this._previewGradeAll = all;
        this._applyUniforms();
        this.requestRender();
    }

    /** Which splats the pick pass may hit (see PICK_OP_*). */
    setPickOp(op: number): void {
        this._pickOp = op;
        for (const mat of this._materials()) mat.setParameter('pickOp', op);
    }

    requestRender(): void {
        const app = this.app as pc.AppBase & { renderNextFrame?: boolean };
        app.renderNextFrame = true;
        // StorySplat viewer's on-demand rendering hook, when present
        (app as unknown as { fire: (n: string) => void }).fire?.('storysplat:requestRender');
    }

    /** Push any model changes to the GPU. Called every prerender; cheap when nothing changed. */
    sync(): void {
        const model = this.model;
        model.setWorldMatrix(this._entity.getWorldTransform().data);

        if (model.rowsVersion !== this._synced.rows) {
            this._build();
            return;
        }
        let changed = false;
        if (model.stateVersion !== this._synced.state) {
            this._uploadState();
            this._synced.state = model.stateVersion;
            this._synced.marks = model.marksVersion;
            this._updateMappingIfNeeded();
            changed = true;
        } else if (model.marksVersion !== this._synced.marks) {
            // preview marks only recolour; visibility (and the sort mapping) is unchanged
            this._uploadState();
            this._synced.marks = model.marksVersion;
            changed = true;
        }
        if (model.transformVersion !== this._synced.transform) {
            this._uploadTransformIndices();
            this._uploadTransformPalette();
            this._updateSortCenters();
            this._synced.transform = model.transformVersion;
            changed = true;
        }
        if (model.colorVersion !== this._synced.color) {
            this._uploadColorIndices();
            this._uploadColorPalette();
            this._synced.color = model.colorVersion;
            changed = true;
        }
        if (changed) this.requestRender();
        const depthOn = hasOtherWorldSplats(this.app);
        if (depthOn !== this.depthLayer.enabled) {
            this.depthLayer.enabled = depthOn;
            this.requestRender();
        }
        this._settleRedraw();
    }

    /** Retire the stand-in once the rebuilt splat draws sorted this frame, then run whenRedrawn callbacks. */
    private _settleRedraw(): void {
        if (this._standIn) {
            const instance = (this._entity.gsplat as unknown as {
                instance?: { sorter?: { pendingSorted?: unknown } | null; meshInstance?: { instancingCount?: number } };
            } | undefined)?.instance;
            // a pending sort is applied right before this frame's draw
            const ready = !!instance && (!!instance.sorter?.pendingSorted || (instance.meshInstance?.instancingCount ?? 0) > 0);
            if (!ready) return;
            this._retireStandIn();
            return;
        }
        this._flushRedrawn();
    }

    private _flushRedrawn(): void {
        if (!this._redrawn.length) return;
        const callbacks = this._redrawn;
        this._redrawn = [];
        for (const callback of callbacks) callback();
    }

    private _retireStandIn(): void {
        const standIn = this._standIn;
        if (!standIn) return;
        this._standIn = null;
        clearTimeout(standIn.timer);
        // callbacks first: a preview parented to the stand-in destroys its own resource
        this._flushRedrawn();
        this._destroyBuild(standIn);
        this.requestRender();
    }

    private _destroyBuild(build: { entity: pc.Entity; resource: pc.GSplatResource | null; textures: pc.Texture[]; depth: DepthDraw | null }): void {
        if (build.depth) this._destroyDepth(build.depth);
        build.entity.destroy();
        for (const t of build.textures) t.destroy();
        build.resource?.destroy();
    }

    /** All gsplat materials owned by the session (main + centers). */
    materials(): pc.Material[] {
        return this._materials();
    }

    destroy(): void {
        this.app.off('prerender', this._onPreRender);
        this.app.scene.off('prerender:layer', this._onPreRenderLayer);
        this._retireStandIn();
        this._flushRedrawn();
        this._destroyOutline();
        this._destroyCenters();
        if (this._depth) this._destroyDepth(this._depth);
        this._depth = null;
        this._entity.destroy();
        this._destroyTextures();
        this._resource?.destroy();
        this._resource = null;
        this._gsplatData = null;
        for (const cam of this._cameras) {
            const layers = cam.layers.filter((id) => id !== this.layer.id && id !== this.overlayLayer.id && id !== this.depthLayer.id);
            cam.layers = layers;
        }
        this.app.scene.layers.remove(this.layer);
        this.app.scene.layers.remove(this.overlayLayer);
        this.app.scene.layers.remove(this.depthLayer);
        this.requestRender();
    }

    // ─── Build ──────────────────────────────────────────────

    private _ensureLayer(name: string, role: SplatEditLayerRole): pc.Layer {
        const layers = this.app.scene.layers;
        let layer = layers.getLayerByName(name);
        if (!layer) {
            layer = new pc.Layer({ name });
            insertSplatEditLayer(layers, layer, role);
        }
        for (const cam of this._cameras) {
            if (!cam.layers.includes(layer.id)) cam.layers = [...cam.layers, layer.id];
        }
        return layer;
    }

    private _applyEntityTransform(): void {
        const m = new pc.Mat4().mul2(this._baseLocal, this._dataTransform);
        const pos = new pc.Vec3();
        const rot = new pc.Quat();
        const scale = new pc.Vec3();
        decompose(m, pos, rot, scale);
        this._entity.setLocalPosition(pos);
        this._entity.setLocalRotation(rot);
        this._entity.setLocalScale(scale);
        for (const e of [this._centersEntity, this._maskEntity]) {
            e?.setLocalPosition(pos);
            e?.setLocalRotation(rot);
            e?.setLocalScale(scale);
        }
        this.requestRender();
    }

    private _build(): void {
        const model = this.model;
        // tear down previous GPU objects (overlays are rebuilt in place)
        this._centersEntity?.removeComponent('gsplat');
        this._maskEntity?.removeComponent('gsplat');
        if (this._entity.gsplat) {
            // keep drawing the previous build on its own entity until the new one has sorted
            const previous = { entity: this._entity, resource: this._resource, textures: this._takeTextures(), depth: this._depth };
            this._depth = null;
            if (this._standIn) {
                // replaced before it ever drew: the older stand-in keeps the screen
                this._destroyBuild(previous);
            } else {
                this._standIn = { ...previous, timer: setTimeout(() => this._retireStandIn(), STAND_IN_TIMEOUT_MS) };
            }
            this._entity = new pc.Entity('splat-edit');
            this._parent.addChild(this._entity);
            this._applyEntityTransform();
        } else {
            this._destroyTextures();
            this._resource?.destroy();
        }
        this._resource = null;

        const properties: { type: string; name: string; storage: Float32Array; byteSize: number }[] = [];
        for (const [name, storage] of model.columns) {
            properties.push({ type: 'float', name, storage, byteSize: 4 });
        }
        this._gsplatData = new pc.GSplatData([{ name: 'vertex', count: model.numSplats, properties } as never]);
        this._resource = new pc.GSplatResource(this.app.graphicsDevice, this._gsplatData);
        const dims = (this._resource as unknown as { streams: { textureDimensions: pc.Vec2 } }).streams.textureDimensions;
        this._dims = { x: dims.x, y: dims.y };

        this._createTextures();

        this._entity.addComponent('gsplat', {
            resource: this._resource,
            unified: false,
            layers: [this.layer.id],
        } as never);
        this._configureMaterial(this._entity);
        this._depth = this._createDepth(this._entity);
        // on-demand rendering: draw the frame that shows the first sort
        const sorter = (this._entity.gsplat as unknown as { instance?: { sorter?: pc.EventHandler | null } }).instance?.sorter;
        sorter?.once('updated', () => this.requestRender());

        if (this._centersEntity) {
            this._centersEntity.addComponent('gsplat', {
                resource: this._resource,
                unified: false,
                layers: [this.overlayLayer.id],
            } as never);
            this._configureMaterial(this._centersEntity, true);
        }
        if (this._maskEntity && this._outline) {
            this._maskEntity.addComponent('gsplat', {
                resource: this._resource,
                unified: false,
                layers: [this._outline.layer.id],
            } as never);
            this._configureMaterial(this._maskEntity, false, true);
        }

        this._synced = { state: -1, marks: -1, transform: -1, color: -1, rows: model.rowsVersion, transformPalette: -1, colorPalette: -1 };
        this._deletedKey = '';
        this.sync();
        this._applyUniforms();
        this.onRebuilt?.();
    }

    private _createCenters(): void {
        const e = new pc.Entity('splat-edit-centers');
        this._entity.parent?.addChild(e);
        this._centersEntity = e;
        this._applyEntityTransform();
        if (this._resource) {
            e.addComponent('gsplat', {
                resource: this._resource,
                unified: false,
                layers: [this.overlayLayer.id],
            } as never);
            this._configureMaterial(e, true);
            this._updateMappingIfNeeded(true);
            this._updateSortCenters();
        }
    }

    private _createOutline(): void {
        const camera = this._cameras[0];
        if (!camera) return;
        this._outline = new SelectionOutline(this.app, camera, this.overlayLayer);
        this._outline.color = this._display.selectedColor;
        const e = new pc.Entity('splat-edit-outline-mask');
        this._entity.parent?.addChild(e);
        this._maskEntity = e;
        this._applyEntityTransform();
        if (this._resource) {
            e.addComponent('gsplat', { resource: this._resource, unified: false, layers: [this._outline.layer.id] } as never);
            this._configureMaterial(e, false, true);
            this._updateMappingIfNeeded(true);
            this._updateSortCenters();
        }
    }

    private _createDepth(entity: pc.Entity): DepthDraw | null {
        const instance = (entity.gsplat as unknown as { instance?: { material: pc.Material; meshInstance: pc.MeshInstance } } | undefined)?.instance;
        const main = instance?.meshInstance;
        // PlayCanvas 2.23 types MeshInstance#mesh as nullable
        if (!instance || !main?.mesh) return null;
        const material = splatEditDepthMaterial(instance.material);
        const meshInstance = new pc.MeshInstance(main.mesh, material, entity);
        // instanced without a vertex buffer, like the gsplat's own draw; never culled
        meshInstance.setInstancing(true as never, false);
        meshInstance.instancingCount = main.instancingCount;
        this.depthLayer.addMeshInstances([meshInstance]);
        return { meshInstance, material, main };
    }

    private _destroyDepth(depth: DepthDraw): void {
        this.depthLayer.removeMeshInstances([depth.meshInstance]);
        depth.meshInstance.destroy();
        depth.material.destroy();
    }

    private _destroyCenters(): void {
        this._centersEntity?.destroy();
        this._centersEntity = null;
    }

    private _destroyOutline(): void {
        this._maskEntity?.destroy();
        this._maskEntity = null;
        this._outline?.destroy();
        this._outline = null;
    }

    private _configureMaterial(entity: pc.Entity, centers = false, mask = false): void {
        const instance = (entity.gsplat as unknown as { instance?: { material: pc.Material } }).instance;
        const material = instance?.material;
        if (!material) throw new Error('SplatEdit: gsplat instance material unavailable');
        const isWebGPU = !!(this.app.graphicsDevice as unknown as { isWebGPU?: boolean }).isWebGPU;
        const chunks = material.getShaderChunks(isWebGPU ? 'wgsl' : 'glsl');
        chunks.set('gsplatCenterVS', isWebGPU ? centerWGSL : centerGLSL);
        chunks.set('gsplatVS', isWebGPU ? vertexWGSL : vertexGLSL);
        chunks.set('gsplatPS', isWebGPU ? fragmentWGSL : fragmentGLSL);
        material.setParameter('alphaClip', 0.1);
        if (centers) {
            material.depthTest = false;
            (material as unknown as { centersOverlay: boolean }).centersOverlay = true;
        }
        material.update();
        this._bindTextures(material);
        (material as unknown as { splatEditRole: string }).splatEditRole = mask ? 'mask' : centers ? 'centers' : 'main';
        this._applyUniformsTo(material, centers, mask);
        if (this._display.shBands >= 0) {
            material.setDefine('SH_BANDS', String(Math.min(this.model.shBands, this._display.shBands)));
            material.update();
        }
    }

    private _materials(): pc.Material[] {
        const out: pc.Material[] = [];
        for (const e of [this._entity, this._centersEntity, this._maskEntity]) {
            const m = (e?.gsplat as unknown as { instance?: { material: pc.Material } } | undefined)?.instance?.material;
            if (m) out.push(m);
        }
        return out;
    }

    // ─── Textures ───────────────────────────────────────────

    private _createTextures(): void {
        const device = this.app.graphicsDevice;
        const { x, y } = this._dims;
        const make = (name: string, format: number, w: number, h: number) => new pc.Texture(device, {
            name, width: w, height: h, format, mipmaps: false,
            minFilter: pc.FILTER_NEAREST, magFilter: pc.FILTER_NEAREST,
            addressU: pc.ADDRESS_CLAMP_TO_EDGE, addressV: pc.ADDRESS_CLAMP_TO_EDGE,
        });
        this._stateTex = make('splatEditState', pc.PIXELFORMAT_R8, x, y);
        this._transformIndexTex = make('splatEditTransformIndex', pc.PIXELFORMAT_R32U, x, y);
        this._colorIndexTex = make('splatEditColorIndex', pc.PIXELFORMAT_R32U, x, y);
        this._transformPaletteTex = make('splatEditTransformPalette', pc.PIXELFORMAT_RGBA32F, 3 * PALETTE_ROW, 1);
        this._colorPaletteTex = make('splatEditColorPalette', pc.PIXELFORMAT_RGBA32F, 4 * PALETTE_ROW, 1);
    }

    /** Hand the current textures to the caller (a stand-in) and forget them. */
    private _takeTextures(): pc.Texture[] {
        const textures = [this._stateTex, this._transformIndexTex, this._colorIndexTex, this._transformPaletteTex, this._colorPaletteTex]
            .filter((t): t is pc.Texture => !!t);
        this._stateTex = this._transformIndexTex = this._colorIndexTex = null;
        this._transformPaletteTex = this._colorPaletteTex = null;
        return textures;
    }

    private _destroyTextures(): void {
        for (const t of [this._stateTex, this._transformIndexTex, this._colorIndexTex, this._transformPaletteTex, this._colorPaletteTex]) {
            t?.destroy();
        }
        this._stateTex = this._transformIndexTex = this._colorIndexTex = null;
        this._transformPaletteTex = this._colorPaletteTex = null;
    }

    private _bindTextures(material: pc.Material): void {
        material.setParameter('splatState', this._stateTex!);
        material.setParameter('splatTransformIndex', this._transformIndexTex!);
        material.setParameter('splatColorIndex', this._colorIndexTex!);
        material.setParameter('transformPalette', this._transformPaletteTex!);
        material.setParameter('colorPalette', this._colorPaletteTex!);
    }

    private _uploadState(): void {
        const tex = this._stateTex;
        if (!tex) return;
        const data = tex.lock() as Uint8Array;
        data.set(this.model.state.subarray(0, Math.min(this.model.numSplats, data.length)));
        tex.unlock();
    }

    private _uploadTransformIndices(): void {
        const tex = this._transformIndexTex;
        if (!tex) return;
        const data = tex.lock() as Uint32Array;
        data.set(this.model.transformIndex.subarray(0, Math.min(this.model.numSplats, data.length)));
        tex.unlock();
    }

    private _uploadColorIndices(): void {
        const tex = this._colorIndexTex;
        if (!tex) return;
        const data = tex.lock() as Uint32Array;
        data.set(this.model.colorIndex.subarray(0, Math.min(this.model.numSplats, data.length)));
        tex.unlock();
    }

    private _uploadTransformPalette(): void {
        const pal = this.model.transformPalette;
        if (pal.version === this._synced.transformPalette) return;
        this._transformPaletteTex = this._uploadPalette(this._transformPaletteTex!, pal.data, pal.count, 3, 'splatEditTransformPalette');
        this._synced.transformPalette = pal.version;
        for (const m of this._materials()) m.setParameter('transformPalette', this._transformPaletteTex);
    }

    private _uploadColorPalette(): void {
        const pal = this.model.colorPalette;
        if (pal.version === this._synced.colorPalette) return;
        this._colorPaletteTex = this._uploadPalette(this._colorPaletteTex!, pal.data, pal.count, 4, 'splatEditColorPalette');
        this._synced.colorPalette = pal.version;
        for (const m of this._materials()) m.setParameter('colorPalette', this._colorPaletteTex);
    }

    private _uploadPalette(tex: pc.Texture, src: Float32Array, count: number, texelsPerEntry: number, name: string): pc.Texture {
        const rows = Math.max(1, Math.ceil(count / PALETTE_ROW));
        if (tex.height < rows) {
            tex.destroy();
            let h = tex.height;
            while (h < rows) h *= 2;
            tex = new pc.Texture(this.app.graphicsDevice, {
                name, width: texelsPerEntry * PALETTE_ROW, height: h, format: pc.PIXELFORMAT_RGBA32F, mipmaps: false,
                minFilter: pc.FILTER_NEAREST, magFilter: pc.FILTER_NEAREST,
                addressU: pc.ADDRESS_CLAMP_TO_EDGE, addressV: pc.ADDRESS_CLAMP_TO_EDGE,
            });
        }
        const data = tex.lock() as Float32Array;
        data.set(src.subarray(0, Math.min(src.length, data.length, count * texelsPerEntry * 4)));
        tex.unlock();
        return tex;
    }

    // ─── Sorting ────────────────────────────────────────────

    private _sorters(): { centers: Float32Array; setMapping: (m: Uint32Array | null) => void; mapping?: Uint32Array | null }[] {
        const out: { centers: Float32Array; setMapping: (m: Uint32Array | null) => void; mapping?: Uint32Array | null }[] = [];
        for (const e of [this._entity, this._centersEntity, this._maskEntity]) {
            const sorter = (e?.gsplat as unknown as { instance?: { sorter?: never } } | undefined)?.instance?.sorter;
            if (sorter) out.push(sorter);
        }
        return out;
    }

    /** Deleted splats leave the sort entirely (fewer splats to sort and draw). */
    private _updateMappingIfNeeded(force = false): void {
        const model = this.model;
        const state = model.state;
        let deleted = 0;
        let sum = 0;
        for (let i = 0; i < model.numSplats; i++) {
            if (state[i] & SplatState.deleted) { deleted++; sum = (sum + i * 2654435761) % 4294967296; }
        }
        const key = `${deleted}:${sum}`;
        if (!force && key === this._deletedKey) return;
        this._deletedKey = key;
        let mapping: Uint32Array | null = null;
        if (deleted > 0) {
            mapping = new Uint32Array(model.numSplats - deleted);
            let o = 0;
            for (let i = 0; i < model.numSplats; i++) if (!(state[i] & SplatState.deleted)) mapping[o++] = i;
        }
        for (const sorter of this._sorters()) {
            sorter.setMapping(mapping ? mapping.slice() : null);
            (sorter as { mapping?: Uint32Array | null }).mapping = mapping;
        }
    }

    /** Moved splats sort by where they are drawn, not where they started. */
    private _updateSortCenters(): void {
        const model = this.model;
        const n = model.numSplats;
        const x = model.col('x'), y = model.col('y'), z = model.col('z');
        const ti = model.transformIndex;
        const pal = model.transformPalette.data;
        const sorters = this._sorters();
        if (!sorters.length) return;
        const centers = new Float32Array(n * 3);
        for (let i = 0; i < n; i++) {
            const t = ti[i];
            const px = x[i], py = y[i], pz = z[i];
            if (t === 0) {
                centers[i * 3] = px; centers[i * 3 + 1] = py; centers[i * 3 + 2] = pz;
            } else {
                const o = t * 12;
                centers[i * 3] = pal[o] * px + pal[o + 1] * py + pal[o + 2] * pz + pal[o + 3];
                centers[i * 3 + 1] = pal[o + 4] * px + pal[o + 5] * py + pal[o + 6] * pz + pal[o + 7];
                centers[i * 3 + 2] = pal[o + 8] * px + pal[o + 9] * py + pal[o + 10] * pz + pal[o + 11];
            }
        }
        for (const sorter of sorters) {
            sorter.centers = centers;
            const mapping = (sorter as { mapping?: Uint32Array | null }).mapping ?? null;
            sorter.setMapping(mapping ? mapping.slice() : null);
        }
    }

    // ─── Uniforms ───────────────────────────────────────────

    private _applyUniforms(): void {
        for (const m of this._materials()) {
            const role = (m as unknown as { splatEditRole?: string }).splatEditRole;
            this._applyUniformsTo(m, role === 'centers', role === 'mask');
        }
    }

    private _applyUniformsTo(material: pc.Material, centers: boolean, mask = false): void {
        const d = this._display;
        material.setParameter('selectedClr', [...d.selectedColor, d.selectionBlend]);
        material.setParameter('unselectedClr', [...d.unselectedColor, d.unselectedBlend]);
        material.setParameter('lockedClr', [...d.lockedColor, d.lockedAlpha]);
        material.setParameter('displayFlags', mask ? [3, 0, 0, 0] : [
            centers ? (d.showCenters ? 1 : 2) : (d.showGaussians ? 1 : 0),
            centers ? 1 : 0,
            d.centerSize,
            centers ? 0 : (d.ringSize > 0 ? d.ringSize : d.selectionRings ? -0.12 : 0),
        ]);
        material.setParameter('pickOp', this._pickOp);
        const pt = this._previewTransform;
        material.setParameter('previewTransform', pt ?? new pc.Mat4().data);
        material.setParameter('previewTransformEnabled', pt ? (this._previewTransformAll ? 2 : 1) : 0);
        const g = this._previewGrade;
        if (g) {
            const rows = gradeRows(g, new Float32Array(12));
            material.setParameter('previewGrade0', Array.from(rows.subarray(0, 4)));
            material.setParameter('previewGrade1', Array.from(rows.subarray(4, 8)));
            material.setParameter('previewGrade2', Array.from(rows.subarray(8, 12)));
            material.setParameter('previewGradeAlpha', [g.transparency, this._previewGradeAll ? 2 : 1]);
        } else {
            material.setParameter('previewGrade0', [1, 0, 0, 0]);
            material.setParameter('previewGrade1', [0, 1, 0, 0]);
            material.setParameter('previewGrade2', [0, 0, 1, 0]);
            material.setParameter('previewGradeAlpha', [1, 0]);
        }
    }
}

/** Decompose an affine matrix into TRS (scale signs folded into rotation where possible). */
function decompose(m: pc.Mat4, pos: pc.Vec3, rot: pc.Quat, scale: pc.Vec3): void {
    m.getTranslation(pos);
    m.getScale(scale);
    // pc.Mat4.getScale returns positive lengths; restore a reflection on one axis if present
    const d = m.data;
    const det = d[0] * (d[5] * d[10] - d[6] * d[9]) - d[4] * (d[1] * d[10] - d[2] * d[9]) + d[8] * (d[1] * d[6] - d[2] * d[5]);
    if (det < 0) scale.x = -scale.x;
    const r = new pc.Mat4().copy(m);
    const rd = r.data;
    const sx = scale.x || 1, sy = scale.y || 1, sz = scale.z || 1;
    rd[0] /= sx; rd[1] /= sx; rd[2] /= sx;
    rd[4] /= sy; rd[5] /= sy; rd[6] /= sy;
    rd[8] /= sz; rd[9] /= sz; rd[10] /= sz;
    rd[12] = 0; rd[13] = 0; rd[14] = 0;
    rot.setFromMat4(r);
}
